import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { StageBadge, TypeBadge, TemperatureBadge } from "@/components/Badges";
import { ContactDialog } from "@/components/ContactDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { relativeDate, fullDate, STAGE_LABELS, STAGE_ORDER } from "@/lib/format";
import { ArrowLeft, Phone, Mail, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

function Field({ icon: Icon, label, value, href }) {
    if (!value) return null;
    const content = href ? <a href={href} className="text-nm-accent hover:underline">{value}</a> : <span className="text-nm-ink">{value}</span>;
    return (
        <div className="flex items-center gap-3 py-2">
            <Icon className="h-4 w-4 shrink-0 text-nm-muted" />
            <div className="min-w-0">
                <p className="text-xs text-nm-muted">{label}</p>
                <p className="truncate text-sm">{content}</p>
            </div>
        </div>
    );
}

export default function ContactoDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/contacts/${id}`);
            setC(data);
        } catch (e) {
            setError(errMsg(e));
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const changeStage = async (stage) => {
        try {
            const { data } = await api.patch(`/contacts/${id}/stage`, { stage });
            setC((prev) => ({ ...prev, ...data }));
            toast.success(`Movido a "${STAGE_LABELS[stage]}"`);
        } catch (e) { toast.error(errMsg(e)); }
    };

    const changeTemperature = async (temperature_override) => {
        try {
            const { data } = await api.put(`/contacts/${id}`, { temperature_override });
            setC((prev) => ({ ...prev, ...data }));
            toast.success("Temperatura actualizada");
        } catch (e) { toast.error(errMsg(e)); }
    };

    const addActivity = async (e) => {
        e.preventDefault();
        if (!note.trim()) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/contacts/${id}/activities`, { note: note.trim() });
            setC(data);
            setNote("");
            toast.success("Conversación registrada");
        } catch (err) { toast.error(errMsg(err)); }
        finally { setSaving(false); }
    };

    const toggleDistributor = async () => {
        try {
            const { data } = await api.put(`/contacts/${id}`, { potential_distributor: !c.potential_distributor });
            setC((prev) => ({ ...prev, ...data }));
        } catch (e) { toast.error(errMsg(e)); }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/contacts/${id}`);
            navigate("/app/contactos");
            toast("Contacto eliminado", {
                description: c.name,
                action: {
                    label: "Deshacer",
                    onClick: async () => {
                        try { await api.post(`/contacts/${id}/restore`); toast.success("Contacto recuperado"); }
                        catch (e) { toast.error(errMsg(e)); }
                    },
                },
                duration: 6000,
            });
        } catch (e) { toast.error(errMsg(e)); }
    };

    if (error) {
        return (
            <div className="py-16 text-center">
                <p className="text-nm-muted">{error}</p>
                <Link to="/app/contactos" className="mt-4 inline-block text-nm-accent">Volver a contactos</Link>
            </div>
        );
    }

    if (!c) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-8 w-40 rounded-lg" />
                <div className="skeleton h-40 w-full rounded-2xl" />
                <div className="skeleton h-64 w-full rounded-2xl" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="contacto-detalle">
            <Link to="/app/contactos" className="inline-flex items-center gap-2 text-sm text-nm-muted hover:text-nm-ink">
                <ArrowLeft className="h-4 w-4" /> Contactos
            </Link>

            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-4">
                    <InitialsAvatar name={c.name} size="lg" />
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-nm-ink">{c.name}</h1>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <TemperatureBadge temperature={c.temperature} />
                            <TypeBadge type={c.type} />
                            <StageBadge stage={c.stage} />
                            {c.potential_distributor && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-nm-accent-soft px-2.5 py-0.5 text-xs font-medium text-nm-accent">
                                    <Star className="h-3 w-3" /> Distribuidor
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-contact-button">
                        <Pencil className="mr-1 h-4 w-4" /> Editar
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="outline" className="text-destructive hover:text-destructive" data-testid="delete-contact-button">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-2xl">
                            <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar a {c.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Podrás deshacerlo durante unos segundos. Se archiva, no se pierde para siempre.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-button">Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                    {/* Etapa y próxima acción */}
                    <section className="rounded-2xl border border-nm-line bg-nm-surface p-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">Etapa</p>
                                <div className="mt-2 w-full sm:w-56">
                                    <Select value={c.stage} onValueChange={changeStage}>
                                        <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {[...STAGE_ORDER, "descartado"].map((s) => (
                                                <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="sm:text-right">
                                <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">Temperatura</p>
                                <div className="mt-2 w-full sm:w-44">
                                    <Select value={c.temperature} onValueChange={changeTemperature}>
                                        <SelectTrigger data-testid="temperature-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="caliente">Caliente</SelectItem>
                                            <SelectItem value="templado">Templado</SelectItem>
                                            <SelectItem value="frio">Frío</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="sm:text-right">
                                <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">Próxima acción</p>
                                <p className="mt-2 font-semibold text-nm-ink">{c.next_action || "—"}</p>
                            </div>
                        </div>
                    </section>

                    {/* Registrar conversación */}
                    <section className="rounded-2xl border border-nm-line bg-nm-surface p-6">
                        <h2 className="text-lg font-semibold text-nm-ink">Registrar conversación</h2>
                        <form onSubmit={addActivity} className="mt-4 space-y-3">
                            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                                placeholder="¿Qué habéis hablado? ¿Qué toca después?" data-testid="activity-input" />
                            <div className="flex justify-end">
                                <Button type="submit" disabled={saving || !note.trim()} data-testid="activity-submit">
                                    {saving ? "Guardando..." : "Guardar"}
                                </Button>
                            </div>
                        </form>

                        <div className="mt-6">
                            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-nm-muted">Historial</p>
                            {(!c.activities || c.activities.length === 0) ? (
                                <p className="rounded-xl border border-dashed border-nm-line p-6 text-center text-sm text-nm-muted">
                                    Todavía no hay conversaciones registradas.
                                </p>
                            ) : (
                                <div className="space-y-4" data-testid="activity-list">
                                    {c.activities.map((a) => (
                                        <div key={a.id} className="flex gap-3">
                                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-nm-accent" />
                                            <div>
                                                <p className="text-sm text-nm-ink">{a.note}</p>
                                                <p className="mt-0.5 text-xs text-nm-muted">{relativeDate(a.created_at)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                {/* Datos */}
                <div className="space-y-6">
                    <section className="rounded-2xl border border-nm-line bg-nm-surface p-6">
                        <h2 className="mb-2 text-lg font-semibold text-nm-ink">Datos</h2>
                        <Field icon={Phone} label="Teléfono" value={c.phone} href={c.phone ? `tel:${c.phone}` : null} />
                        <Field icon={Mail} label="Correo" value={c.email} href={c.email ? `mailto:${c.email}` : null} />
                        {c.source && (
                            <div className="border-t border-nm-line py-3">
                                <p className="text-xs text-nm-muted">Origen</p>
                                <p className="text-sm text-nm-ink">{c.source}</p>
                            </div>
                        )}
                        {c.notes && (
                            <div className="border-t border-nm-line py-3">
                                <p className="text-xs text-nm-muted">Notas</p>
                                <p className="text-sm leading-relaxed text-nm-ink">{c.notes}</p>
                            </div>
                        )}
                        <div className="border-t border-nm-line pt-3">
                            <button onClick={toggleDistributor} data-testid="toggle-distributor"
                                className="flex w-full items-center justify-between text-sm">
                                <span className="text-nm-ink">Posible distribuidor</span>
                                <span className={c.potential_distributor ? "font-semibold text-nm-accent" : "text-nm-muted"}>
                                    {c.potential_distributor ? "Sí" : "No"}
                                </span>
                            </button>
                        </div>
                        <p className="mt-3 border-t border-nm-line pt-3 text-xs text-nm-muted">
                            Añadido el {fullDate(c.created_at)}
                        </p>
                    </section>
                </div>
            </div>

            <ContactDialog open={editOpen} onOpenChange={setEditOpen} contact={c} onSaved={(data) => setC((prev) => ({ ...prev, ...data }))} />
        </div>
    );
}
